import { ROUTE_CONFIG } from "@/routes/config";

import { DashboardIssue } from "../schema/dashboard";

/** Structurally an `IssueDisplayProps`; kept apart so this module does not import its caller. */
export interface CrmReconnectIssueDisplay {
	title: string;
	description: string;
	primaryActionLabel: string;
	primaryActionTo: string;
}

const CRMS = {
	hubspot: { name: "HubSpot", to: ROUTE_CONFIG.INTEGRATIONS_HUBSPOT },
	zoho: { name: "Zoho CRM", to: ROUTE_CONFIG.INTEGRATIONS_ZOHO },
	pipedrive: { name: "Pipedrive", to: ROUTE_CONFIG.INTEGRATIONS_PIPEDRIVE },
} as const;

type ReconnectCrm = keyof typeof CRMS;

const crmsOf = (issue: DashboardIssue): ReconnectCrm[] => {
	const crms = issue.payload?.crms;

	return Array.isArray(crms)
		? crms.filter((crm): crm is ReconnectCrm => typeof crm === "string" && crm in CRMS)
		: [];
};

/**
 * The card for a CRM whose stored sign-in the CRM refuses (PREC-4353): nothing syncs until the
 * company reconnects, and a new sign-in keeps the settings and field mappings.
 */
export function crmReconnectIssueDisplay(issue: DashboardIssue): CrmReconnectIssueDisplay {
	const crms = crmsOf(issue);
	const names = crms.map((crm) => CRMS[crm].name).join(" and ") || "Your CRM";
	const verb = crms.length > 1 ? "no longer accept" : "no longer accepts";

	return {
		title: `Reconnect ${crms.length > 0 ? names : "your CRM"}`,
		description: `${names} ${verb} the stored sign-in, so nothing syncs until you reconnect. Your settings and field mappings are kept.`,
		primaryActionLabel: "Reconnect",
		primaryActionTo: crms.length > 0 ? CRMS[crms[0]].to : ROUTE_CONFIG.INTEGRATIONS,
	};
}
